import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomButton from "../../Button/CustomButton";
import toast from "react-hot-toast";

const ViewApiKeyDetailsModal = ({ open, onClose, data }) => {
  if (!data) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(data.masked_key);
    toast.success("API key copied to clipboard!");
  };

  const getStatusColor = (status) => {
    return status === "active" ? "success" : "default";
  };

  const getTypeColor = (type) => {
    const colors = {
      public: { bg: "#E3F2FD", color: "#1565C0" },
      private: { bg: "#F3E5F5", color: "#7B1FA2" },
    };
    return colors[type] || { bg: "#F5F5F5", color: "#666" };
  };

  const transformPermissions = () => {
    if (!data.permissions) return [];
    return Object.entries(data.permissions)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);
  };

  const typeColors = getTypeColor(data.type);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <VpnKeyIcon fontSize="medium" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {data.name}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <Chip
                  label={data.type.toUpperCase()}
                  size="small"
                  sx={{
                    backgroundColor: typeColors.bg,
                    color: typeColors.color,
                    fontWeight: 600,
                    fontSize: 10,
                    height: 20,
                  }}
                />
                <Chip
                  label={data.status.toUpperCase()}
                  size="small"
                  color={getStatusColor(data.status)}
                  sx={{ fontWeight: 500, fontSize: 10, height: 20 }}
                />
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
              API KEY
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                backgroundColor: "#F9FAFB",
                borderRadius: 1,
                border: "1px solid #E0E0E0",
                mt: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: 13,
                  color: "#666",
                }}
              >
                {data.masked_key}
              </Typography>
              <IconButton size="small" onClick={handleCopyKey} sx={{ color: "primary.main" }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
              GENERAL INFORMATION
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <InfoRow label="Key ID" value={data._id} />
              </Grid>
              <Grid item xs={6}>
                <InfoRow label="Prefix" value={data.prefix} />
              </Grid>
              <Grid item xs={6}>
                <InfoRow label="Created At" value={formatDate(data.created_at)} />
              </Grid>
              <Grid item xs={6}>
                <InfoRow label="Updated At" value={formatDate(data.updated_at)} />
              </Grid>
              {data.org_id && (
                <Grid item xs={6}>
                  <InfoRow label="Organization ID" value={data.org_id} />
                </Grid>
              )}
              {data.user_id && (
                <Grid item xs={6}>
                  <InfoRow label="User ID" value={data.user_id} />
                </Grid>
              )}
              {data.owner_id && (
                <Grid item xs={6}>
                  <InfoRow label="Owner ID" value={data.owner_id} />
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
              PERMISSIONS
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {transformPermissions().length > 0 ? (
                transformPermissions().map((permission, idx) => (
                  <Chip
                    key={idx}
                    label={permission}
                    size="small"
                    sx={{
                      backgroundColor: "#E8F5E9",
                      color: "#2E7D32",
                      fontWeight: 500,
                      fontSize: 11,
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No permissions assigned
                </Typography>
              )}
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
              RATE LIMITS
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <InfoRow
                  label="Requests Per Minute"
                  value={data.rate_limit?.requests_per_minute || "N/A"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoRow
                  label="Concurrent Calls"
                  value={data.rate_limit?.concurrent_calls || "N/A"}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
              USAGE STATISTICS
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <InfoRow label="Total Calls" value={data.usage_stats?.total_calls || 0} />
              </Grid>
              <Grid item xs={6}>
                <InfoRow label="Last Used" value={formatDate(data.usage_stats?.last_used)} />
              </Grid>
              {data.usage_stats?.window_count !== undefined && (
                <Grid item xs={6}>
                  <InfoRow label="Window Count" value={data.usage_stats.window_count} />
                </Grid>
              )}
              {data.usage_stats?.window_start && (
                <Grid item xs={6}>
                  <InfoRow
                    label="Window Start"
                    value={formatDate(data.usage_stats.window_start)}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {data.ivr_config && Object.keys(data.ivr_config).length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                  IVR CONFIGURATION
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {data.ivr_config.default_llm && (
                    <Grid item xs={6}>
                      <InfoRow label="Default LLM" value={data.ivr_config.default_llm} />
                    </Grid>
                  )}
                  {data.ivr_config.voice_provider && (
                    <Grid item xs={6}>
                      <InfoRow label="Voice Provider" value={data.ivr_config.voice_provider} />
                    </Grid>
                  )}
                  {data.ivr_config.debug_mode !== undefined && (
                    <Grid item xs={6}>
                      <InfoRow
                        label="Debug Mode"
                        value={data.ivr_config.debug_mode ? "Enabled" : "Disabled"}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <CustomButton text="Close" variant="contained" onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
};

const InfoRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{
        mt: 0.5,
        wordBreak: "break-all",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default ViewApiKeyDetailsModal;
